import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import { ThemeToggle } from '@/components/ThemeToggle';
import { UrlHistoryDropdown } from '@/components/UrlHistoryDropdown';
import { RecentArticles } from '@/components/RecentArticles';
import { MessageSquare, Search } from 'lucide-react';
import { cn, urlToNavigationPath } from '@/lib/utils';

const Index = () => {
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useSeoMeta({
    title: 'WebVoice - Comment on Any URL with Nostr',
    description: 'Join the conversation on any website using the Nostr protocol. Comment, discuss, and connect across the decentralized web.',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError('');

    if (!urlInput.trim()) {
      setUrlError('Please enter a URL');
      return;
    }

    try {
      // Add https:// if no protocol is specified
      let urlToValidate = urlInput.trim();
      if (!urlToValidate.match(/^https?:\/\//)) {
        urlToValidate = `https://${urlToValidate}`;
      }

      const url = new URL(urlToValidate);
      const navigationPath = urlToNavigationPath(url.toString());
      navigate(navigationPath);
    } catch {
      setUrlError('Please enter a valid URL');
    }
  };

  const handleArticleClick = (articleUrl: string) => {
    const navigationPath = urlToNavigationPath(articleUrl);
    navigate(navigationPath);
  };

  const handleNavigateFromHistory = (url: string) => {
    const navigationPath = urlToNavigationPath(url);
    navigate(navigationPath);
  };

  const handleInputFocus = () => {
    setShowHistory(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlInput(e.target.value);
    if (e.target.value.trim() === '') {
      setShowHistory(true);
    } else {
      setShowHistory(false);
    }
  };



  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Simple Header */}
      <header className="px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center space-x-2">
            <MessageSquare className="h-6 w-6 text-blue-600" />
            <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
              WebVoice
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            <LoginArea className="max-w-60" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section - Google-style centered */}
          <div className="flex flex-col items-center justify-center min-h-[50vh] mb-12">
            <div className="w-full max-w-2xl space-y-8">
          {/* Logo and Title */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center w-20 h-20 bg-blue-600 rounded-2xl mx-auto">
              <MessageSquare className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl font-light text-gray-900 dark:text-gray-100">
              WebVoice
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Comment on any URL with Nostr
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                ref={inputRef}
                type="text"
                placeholder="Enter any URL to view comments"
                value={urlInput}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                className={cn(
                  "pl-12 pr-4 py-4 text-lg rounded-full border-2 border-gray-200 dark:border-gray-700 focus:border-blue-500 dark:focus:border-blue-400 shadow-sm hover:shadow-md transition-shadow",
                  urlError && "border-red-500 focus:border-red-500"
                )}
              />
              <UrlHistoryDropdown
                isOpen={showHistory}
                onClose={() => setShowHistory(false)}
                onNavigateToUrl={handleNavigateFromHistory}
                inputRef={inputRef}
              />
            </div>

            {urlError && (
              <p className="text-sm text-red-500 text-center">{urlError}</p>
            )}

            <div className="flex justify-center space-x-4">
              <Button
                type="submit"
                className="px-8 py-3 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300 hover:shadow-md transition-all dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                variant="outline"
              >
                View Comments
              </Button>
            </div>
          </form>

            </div>
          </div>

          {/* Recent Articles Section */}
          <div className="w-full">
            <RecentArticles onArticleClick={handleArticleClick} />
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="text-center py-8 text-sm text-gray-500 dark:text-gray-400 space-x-6">
        <a
          href="https://nostr.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 dark:hover:text-gray-300"
        >
          Powered by Nostr
        </a>
        <a
          href="https://soapbox.pub/mkstack"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-gray-700 dark:hover:text-gray-300"
        >
          Vibed with MKStack
        </a>
      </footer>
    </div>
  );
};

export default Index;
